import { Info } from "lucide-react";
import { useState } from "react";

const priorityOptions = [
  {
    value: 2,
    title: "Tier 2",
    description: "- Moderate concern, monitor situation",
  },
  {
    value: 3,
    title: "Tier 3",
    description: "- High priority, immediate intervention needed",
  },
];

export default function AddWordAlert({
  show,
  onClose,
  data,
  createWordsMutation,
  user,
}) {
  // Updated initial visibility state based on new role restrictions
  const [visibility, setVisibility] = useState(() => {
    if (user?.role === "123wellness") {
      return "default";
    }

    if (user?.role === "superadmin") {
      return "district";
    }

    if (user?.role === "admin") {
      return "school";
    }

    return "custom";
  });

  const [priority, setPriority] = useState("");
  const [wordsInput, setWordsInput] = useState("");

  const parsedWords = wordsInput
    .split(/[;,\n]/)
    .map((w) => w.trim().toLowerCase())
    .filter((w) => w.length > 0)
    .filter((w) => w.length >= 2) // Prevent single character words
    .filter((w) => /^[a-z0-9\s\-']+$/i.test(w)); // Only allow letters, numbers, spaces, hyphens, and apostrophes

  // For comprehensive duplicate checking, we need to check against:
  // 1. Words that already exist regardless of tier, scope, etc.
  const wouldBeDuplicates = parsedWords.filter((word) => {
    // Check if this word already exists in any form in the rules
    const isDuplicate =
      data?.some((rule) => rule.word.toLowerCase() === word.toLowerCase()) ||
      false;

    return isDuplicate;
  });

  const newWords = parsedWords.filter(
    (word) => !wouldBeDuplicates.includes(word),
  );
  const duplicateWords = wouldBeDuplicates;

  const existingAlerts = parsedWords
    .map((word) => {
      const found = data?.find(
        (r) => r.word?.toLowerCase() === word.toLowerCase(),
      );
      return found ? { ...found, word } : null;
    })
    .filter(Boolean);
  const hasExisting = existingAlerts.length > 0;

  const [isSaving, setIsSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const handleSaveAlert = async () => {
    if (isSaving) return;
    setIsSaving(true);
    setErrorMsg("");

    try {
      // Validation
      if (parsedWords.length === 0) {
        if (wordsInput.trim().length === 0) {
          setErrorMsg("Please enter at least one word to monitor.");
        } else {
          setErrorMsg(
            "No valid words found. Words must be at least 2 characters long and contain only letters, numbers, spaces, hyphens, and apostrophes.",
          );
        }
        return;
      }

      if (newWords.length === 0 && parsedWords.length > 0) {
        setErrorMsg(
          "All entered words already exist in your word alerts. Please enter new words.",
        );
        return;
      }

      if (!priority) {
        setErrorMsg("Please select an alert priority.");
        return;
      }

      if (!visibility) {
        setErrorMsg("Please select rule visibility.");
        return;
      }

      // Validate visibility based on role
      try {
        validateVisibility(visibility);
      } catch (error) {
        setErrorMsg(error.message);
        return;
      }

      if (!createWordsMutation) {
        setErrorMsg("Save function not available. Please refresh the page.");
        return;
      }

      // Client-side duplicate validation: check if any words already exist
      // This is our primary duplicate prevention mechanism
      const existingDuplicates = newWords.filter((word) =>
        data?.some(
          (rule) =>
            rule.word.trim().toLowerCase() === word.trim().toLowerCase(),
        ),
      );

      if (existingDuplicates.length > 0) {
        setErrorMsg(
          `Cannot add words that already exist: ${existingDuplicates.join(
            ", ",
          )}. Words cannot be duplicated even with different tiers or settings.`,
        );
        return;
      }

      // Additional check: prevent duplicates within the current input
      const inputDuplicates = newWords.filter(
        (word, index) => newWords.indexOf(word) !== index,
      );

      if (inputDuplicates.length > 0) {
        setErrorMsg(
          `Duplicate words in your input: ${[...new Set(inputDuplicates)].join(
            ", ",
          )}. Please remove duplicates and try again.`,
        );
        return;
      }

      // Validate required IDs based on scope
      let entityId;
      if (visibility === "school") {
        if (!user.schools?.[0]) {
          setErrorMsg(
            "School ID is required for school-wide rules. Please check your permissions.",
          );
          return;
        }
        entityId = user.schools?.[0]?._id;
      } else if (visibility === "district") {
        if (!user.districts?.[0]) {
          setErrorMsg(
            "District ID is required for district-wide rules. Please check your permissions.",
          );
          return;
        }
        entityId = user.districts?.[0]?._id;
      } else if (visibility === "custom") {
        if (user?.role === "teacher") {
          entityId = user?.classes.map((c) => c._id);
        } else if (user?.role === "admin") {
          entityId = user.schools?.[0]?._id;
        } else if (user?.role === "superadmin") {
          entityId = user?.districts?.[0]?._id;
        }
      } else {
        entityId = null; // default scope
      }

      // Create words array for the new createWordsMutation API
      const successfulWords = [];
      const failedWords = [];

      try {
        // Prepare all words for batch creation
        const wordsData = newWords.map((word) => ({
          word: word.trim(),
          tier: priority,
          scope: visibility,
          entityId,
        }));

        // Use the new createWordsMutation that expects an array
        await createWordsMutation.mutateAsync(wordsData);
        successfulWords.push(...newWords);
      } catch (batchError) {
        // Individual word creation with client-side duplicate checking for each word
        for (const word of newWords) {
          try {
            // Double-check this specific word doesn't exist (extra safety)
            const wordExists = data?.some(
              (rule) =>
                rule.word.trim().toLowerCase() === word.trim().toLowerCase(),
            );
            if (wordExists) {
              failedWords.push(`${word} (already exists)`);
              continue;
            }

            const wordData = [
              {
                word: word.trim(),
                tier: parseInt(priority),
                scope: visibility,
                entityId: entityId,
              },
            ];

            await createWordsMutation.mutateAsync(wordData);
            successfulWords.push(word);
          } catch (wordError) {
            failedWords.push(`${word} (${getErrorMessage(wordError)})`);
          }
        }
      }

      // Show results inline
      if (successfulWords.length > 0 && failedWords.length > 0) {
        setErrorMsg(
          `Partially successful: ${successfulWords.length} word(s) added, ${
            failedWords.length
          } failed: ${failedWords.join(", ")}`,
        );
      } else if (failedWords.length > 0) {
        setErrorMsg(
          `Failed to add word(s): ${failedWords.join(
            ", ",
          )}. Please check for duplicates or try again.`,
        );
      } else {
        // Success - close the modal
        onClose();
      }
    } catch (error) {
      setErrorMsg(getErrorMessage(error));
    } finally {
      setIsSaving(false);
    }
  };

  // Helper function to validate visibility based on role
  const validateVisibility = (newVisibility) => {
    if (user.role === "123wellness" && newVisibility !== "default") {
      throw new Error("123wellness users can only create default rules.");
    }
    if (
      user.role === "superadmin" &&
      !["district", "custom"].includes(newVisibility)
    ) {
      throw new Error(
        "Superadmins can only create district-wide / custom rules.",
      );
    }
    if (
      user.role === "admin" &&
      !["school", "custom"].includes(newVisibility)
    ) {
      throw new Error("Admins can only create school-wide / custom rules.");
    }
    if (user.role === "teacher" && newVisibility !== "custom") {
      throw new Error("Teachers can only create custom rules.");
    }
  };

  // Helper function to get user-friendly error messages
  const getErrorMessage = (error) => {
    if (error?.message?.includes("Teacher entityId")) {
      return "You must be assigned to at least one class to create custom alerts.";
    }
    if (error?.message?.includes("School entityId")) {
      return "Missing required school information";
    }
    if (error?.message?.includes("District entityId")) {
      return "Missing required district information";
    }
    if (error?.message?.toLowerCase()?.includes("duplicate")) {
      return "This word already exists in the system";
    }
    if (error?.message?.includes("Valid entityId is required")) {
      return "Invalid ID format for rule scope";
    }
    if (error?.message?.includes("validation")) {
      return error.message;
    }
    return error?.message || "Unknown error occurred";
  };

  function getScopeLabel(scope) {
    if (scope === "default") return "Default";
    if (scope === "district") return "District-wide";
    if (scope === "school") return "School-wide";
    if (scope === "class") return "Custom Rule";
    return scope;
  }

  if (!show) return null;

  return (
    <div className="fixed inset-0 z-10 flex items-center justify-center bg-black/30">
      <div className="bg-white rounded-xl sm:ml-[130px] w-[90%] sm:w-[50%] xl:w-[40%] 2xl:w-[30%] shadow-xl relative">
        <div className="text-[18px] flex py-2 px-4 border-b border-b-[#DDDDDD] sticky z-10 top-0 bg-white">
          ADD WORD ALERT
        </div>
        <div className="py-3 px-4 flex flex-col gap-3 sm:max-h-[75vh] max-h-[60vh] overflow-y-auto">
          <div className="flex flex-col items-center gap-2 bg-[#FBFDFD] border border-[#DDDDDD59] p-2 rounded">
            <div className="flex flex-row items-center gap-2">
              <Info className="w-4 h-4 opacity-50" />
              <div className="font-semibold text-[14px]">
                How Word Alerts Work
              </div>
            </div>
            <p className="text-[14px]">
              Monitor student responses for specific keywords you define and
              help identify students who may need immediate support. When these
              words appear in student feedback you will receive a notification.
            </p>
          </div>

          <div>
            <div className="text[16px] font-semibold mb-2 block">
              Word(s) to Monitor
            </div>
            <div className="w-full">
              <textarea
                className="w-full border border-[#DDDDDD] rounded p-2 text-[14px] text-black/2 focus:outline-none focus:ring-2 focus:ring-[#156E45] min-h-32 resize-y bg-white"
                placeholder={
                  "Separate words with commas, semicolons, or new lines. Each word/phrase will be added as a separate item to monitor..."
                }
                value={wordsInput}
                onChange={(e) => setWordsInput(e.target.value)}
              />
            </div>
          </div>

          {parsedWords.length > 0 && (
            <div className="flex flex-col text-[16px] border border-[#DDDDDD] p-2 gap-2">
              {newWords.length > 0 && (
                <>
                  <div className="font-semibold">
                    New word(s) to be added ({newWords.length})
                  </div>
                  <div className="flex flex-row flex-wrap items-center gap-2">
                    {newWords.map((word, idx) => (
                      <span
                        key={word + idx}
                        className="rounded-2xl px-2 py-1 bg-[#EBF5FE] "
                      >
                        {word}
                      </span>
                    ))}
                  </div>
                </>
              )}

              {duplicateWords.length > 0 && (
                <>
                  <div className="font-semibold text-[#A05D64]">
                    Duplicate word(s) (will be skipped) ({duplicateWords.length}
                    )
                  </div>
                  <div className="flex flex-row flex-wrap items-center gap-2">
                    {duplicateWords.map((word, idx) => (
                      <span
                        key={word + idx}
                        className="rounded-2xl px-2 py-1 bg-[#FFE6E6] text-[#A05D64]"
                      >
                        {word}
                      </span>
                    ))}
                  </div>
                  <div className="text-[#A05D64] text-[14px]">
                    These words already exist in your word alerts and will not
                    be added again.
                  </div>
                </>
              )}
            </div>
          )}

          <div>
            <div className="text-[16px] font-semibold mb-2">
              Rule Visibility
            </div>
            <div className="flex flex-col gap-2 text-[14px]">
              {user?.role === "123wellness" ? (
                <div className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="visibility"
                    value="default"
                    checked
                    readOnly
                  />
                  <span className="font-semibold">Default Rule</span> -
                  Applicable to all users
                </div>
              ) : user?.role === "teacher" ? (
                <div className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="visibility"
                    value="custom"
                    checked
                    readOnly
                  />
                  <span className="font-semibold">Custom Rule</span> - Only you
                  will see this alert
                </div>
              ) : (
                <>
                  {user?.role === "superadmin" && (
                    <div className="flex items-center gap-2">
                      <input
                        type="radio"
                        name="visibility"
                        value="district"
                        checked={visibility === "district"}
                        onChange={() => setVisibility("district")}
                      />
                      <span className="font-semibold">District-wide Rule</span>{" "}
                      - All schools in district will see this alert
                    </div>
                  )}
                  {user?.role === "admin" && (
                    <div className="flex items-center gap-2">
                      <input
                        type="radio"
                        name="visibility"
                        value="school"
                        checked={visibility === "school"}
                        onChange={() => setVisibility("school")}
                      />
                      <span className="font-semibold">School-wide Rule</span> -
                      All teachers will see this alert
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="visibility"
                      value="custom"
                      checked={visibility === "custom"}
                      onChange={() => setVisibility("custom")}
                    />
                    <span className="font-semibold">Custom Rule</span> - Only
                    you will see this alert
                  </div>
                </>
              )}
            </div>
          </div>

          <div>
            <div className="text-[16px] font-semibold mb-2">Alert Priority</div>
            <div className="flex flex-col gap-2 text-[14px]">
              {priorityOptions.map((option) => (
                <div key={option.value} className="flex gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="tier"
                    value={option.value}
                    checked={priority === option.value}
                    onChange={() => setPriority(option.value)}
                  />
                  <span className="font-semibold">{option.title}</span>
                  <span>{option.description}</span>
                </div>
              ))}
            </div>
          </div>

          {hasExisting && (
            <div className="flex flex-col gap-2">
              <div className="text-[#A05D64]">
                An alert for this word already exists.
              </div>

              <div className="border border-[#DDDDDD] bg-[#FAFAFA] rounded p-2 text-[16px]">
                <div className="font-semibold mb-2">Current Alert</div>
                {existingAlerts.map((alert, idx) => (
                  <div
                    key={alert.word + idx}
                    className="mb-1 flex items-center gap-2"
                  >
                    Word:
                    <span>"{alert.word}"</span>
                    <span className="font-bold">• </span>
                    {alert.tier === "tier2"
                      ? "Tier 2"
                      : alert.tier === "tier3"
                        ? "Tier 3"
                        : alert.tier}
                    <span className="font-bold">•</span>
                    <span className="capitalize">
                      {getScopeLabel(alert.scope)}
                    </span>
                  </div>
                ))}
              </div>

              <div className="border border-[#DDDDDD] bg-[#F1F7FC] rounded p-2">
                <div className="font-semibold mb-2">New Alert</div>
                {existingAlerts.map((alert, idx) => (
                  <div
                    key={alert.word + idx}
                    className="mb-1 flex items-center gap-2"
                  >
                    <span>Word:</span>
                    <span>"{alert.word}"</span>
                    {priority !== "" && <span className="font-bold">• </span>}
                    {priority === "tier2"
                      ? "Tier 2"
                      : priority === "tier3"
                        ? "Tier 3"
                        : priority}
                    {visibility !== "" && <span className="font-bold">• </span>}
                    {getScopeLabel(
                      visibility === "custom"
                        ? "Custom"
                        : visibility === "school"
                          ? "School-wide"
                          : visibility === "district"
                            ? "District-wide"
                            : "Default",
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        {visibility === "school" && (
          <div className="text-[#A05D64] mx-8 mt-4">
            All teachers will receive this alert when it's triggered. Are you
            sure you want to proceed?
          </div>
        )}
        {visibility === "default" && (
          <div className="text-[#A05D64] mx-8 mt-4">
            This will be a system-wide default rule visible to all users. Are
            you sure you want to proceed?
          </div>
        )}

        {errorMsg && (
          <div className="text-red-600 text-[15px] px-8 pb-2">{errorMsg}</div>
        )}
        <div className="flex gap-3 mx-4 my-2">
          <button
            onClick={handleSaveAlert}
            disabled={createWordsMutation.isPending || isSaving}
            className={`py-1 px-3 rounded text-[16px] font-semibold transition-colors ${
              createWordsMutation.isPending || isSaving
                ? "bg-gray-400 text-gray-200 cursor-not-allowed"
                : "bg-[#156E45] text-white hover:bg-[#0d5a37]"
            }`}
          >
            {createWordsMutation.isPending || isSaving
              ? "Saving..."
              : "Save Alert"}
          </button>
          <button
            onClick={onClose}
            className="py-1 px-3 rounded text-[16px] font-semibold transition-colors bg-gray-300 text-gray-700 hover:bg-gray-400"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
